/**
 * PrizeTrackingDashboard Component
 * Premium, clean interface for tracking fishing equipment prize deliveries
 * Features: Simplified design, enhanced UX, WCAG AA compliant, mobile-optimized
 */

import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter,
  Search,
  ArrowLeft,
  Fish,
  Trophy,
  ExternalLink,
  Calendar,
  ChevronRight,
  Shield,
  MapPin,
  Phone,
  Mail
} from 'lucide-react'
import { Button, Input, Modal, Badge, LoadingSkeleton } from '../ui'
import { usePrizeDeliveryStore } from '../../stores/prizeDeliveryStore'
import { useAuthStore } from '../../stores/authStore'
import type { UserPrizeTracking } from '../../types/prizeDelivery'

interface PrizeTrackingDashboardProps {
  className?: string
  onClose?: () => void
}

// Clean filter options with better contrast and accessibility
const filterOptions = [
  {
    value: 'all',
    label: 'Todos',
    icon: Trophy,
    className: 'bg-gray-50 text-gray-900 border-gray-200 hover:bg-gray-100',
    activeClassName: 'bg-primary-600 text-white border-primary-600'
  },
  {
    value: 'pending',
    label: 'Preparando',
    icon: Package,
    className: 'bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-100',
    activeClassName: 'bg-blue-600 text-white border-blue-600'
  },
  {
    value: 'in-progress',
    label: 'En camino',
    icon: Truck,
    className: 'bg-orange-50 text-orange-900 border-orange-200 hover:bg-orange-100',
    activeClassName: 'bg-orange-600 text-white border-orange-600'
  },
  {
    value: 'completed',
    label: 'Entregados',
    icon: CheckCircle,
    className: 'bg-green-50 text-green-900 border-green-200 hover:bg-green-100',
    activeClassName: 'bg-green-600 text-white border-green-600'
  }
]

// Clean delivery card component with premium, simple design
const DeliveryCard: React.FC<{
  delivery: UserPrizeTracking
  onClick: () => void
}> = ({ delivery, onClick }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          color: 'text-blue-700',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          badgeVariant: 'info' as const,
          Icon: Package
        }
      case 'in-progress':
        return {
          color: 'text-orange-700',
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          badgeVariant: 'warning' as const,
          Icon: Truck
        }
      case 'completed':
        return {
          color: 'text-green-700',
          bg: 'bg-green-50',
          border: 'border-green-200',
          badgeVariant: 'success' as const,
          Icon: CheckCircle
        }
      default:
        return {
          color: 'text-gray-700',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          badgeVariant: 'default' as const,
          Icon: Clock
        }
    }
  }

  const config = getStatusConfig(delivery.overallStatus)
  const StatusIcon = config.Icon

  return (
    <motion.article
      layout
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      aria-label={`Ver detalles del premio ${delivery.raffleTitle}, número ganador ${delivery.winningNumber}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <div className={`p-2.5 rounded-lg ${config.bg} ${config.color}`}>
              <StatusIcon className="w-5 h-5" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {delivery.raffleTitle}
              </h3>
              <p className="text-sm text-gray-600">
                Número ganador: <span className="font-semibold text-gray-900">#{delivery.winningNumber.padStart(2, '0')}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="text-right ml-4">
          <div className="text-xl font-bold text-gray-900 mb-1">
            {delivery.totalValue}€
          </div>
          <Badge variant={config.badgeVariant} size="sm">
            {filterOptions.find(f => f.value === delivery.overallStatus)?.label || 'Estado'}
          </Badge>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progreso</span>
          <span className="text-sm font-semibold text-gray-900">{Math.round(delivery.progressPercentage)}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${Math.round(delivery.progressPercentage)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Delivery info */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500 mb-1">Entregas completadas</div>
          <div className="font-medium text-gray-900">
            {delivery.deliveries.filter(d => d.status === 'delivered').length} de {delivery.deliveries.length}
          </div>
        </div>
        {delivery.nextDeliveryDate && (
          <div>
            <div className="text-gray-500 mb-1">Próxima entrega</div>
            <div className="font-medium text-gray-900">
              {new Intl.DateTimeFormat('es-ES', {
                day: 'numeric',
                month: 'short'
              }).format(delivery.nextDeliveryDate)}
            </div>
          </div>
        )}
      </div>

      {/* Hover indicator */}
      <ChevronRight className="absolute top-6 right-6 w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true" />
    </motion.article>
  )
}

// Clean delivery detail modal with improved UX
const DeliveryDetailModal: React.FC<{
  delivery: UserPrizeTracking | null
  isOpen: boolean
  onClose: () => void
}> = ({ delivery, isOpen, onClose }) => {
  if (!delivery) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalles de la entrega"
      size="lg"
      closeOnBackdropClick={true}
      closeOnEscape={true}
    >
      <div className="space-y-6">
        {/* Prize Header */}
        <header className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1">{delivery.raffleTitle}</h3>
              <p className="text-gray-600">
                Número ganador: <span className="font-semibold text-gray-900">#{delivery.winningNumber.padStart(2, '0')}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{delivery.totalValue}€</div>
              <p className="text-sm text-gray-500">Valor total</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Progreso general</span>
              <span className="text-sm font-semibold text-primary-700">{Math.round(delivery.progressPercentage)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${Math.round(delivery.progressPercentage)}%` }}
              />
            </div>
          </div>
        </header>

        {/* Delivery Timeline */}
        <section>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Truck className="w-5 h-5 text-primary-600 mr-2" aria-hidden="true" />
            Cronología de entregas
          </h4>

          <div className="space-y-4" role="list" aria-label="Cronología de entregas">
            {delivery.deliveries
              .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
              .map((item, index) => {
              const isCompleted = item.status === 'delivered'
              const isInProgress = ['preparing', 'shipped'].includes(item.status)

              return (
                <div key={item.id} className="relative" role="listitem">
                  {/* Timeline connector */}
                  {index < delivery.deliveries.length - 1 && (
                    <div className="absolute left-5 top-12 w-0.5 h-12 bg-gray-200" aria-hidden="true" />
                  )}

                  <div className="flex items-start space-x-4">
                    {/* Status indicator */}
                    <div className={`
                      flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2
                      ${isCompleted
                        ? 'bg-green-50 border-green-200 text-green-600'
                        : isInProgress
                        ? 'bg-orange-50 border-orange-200 text-orange-600'
                        : 'bg-gray-50 border-gray-200 text-gray-400'}
                    `} aria-hidden="true">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isInProgress ? (
                        <Truck className="w-5 h-5" />
                      ) : (
                        <Clock className="w-5 h-5" />
                      )}
                    </div>

                    {/* Delivery content */}
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-1">{item.title}</h5>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                        <Badge
                          variant={isCompleted ? 'success' : isInProgress ? 'warning' : 'default'}
                          size="sm"
                        >
                          {isCompleted ? 'Entregado' : isInProgress ? 'En camino' : 'Pendiente'}
                        </Badge>
                      </div>

                      {/* Tracking information */}
                      {item.trackingNumber && (
                        <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                                Número de seguimiento
                              </label>
                              <code className="text-sm font-mono font-semibold text-gray-900 select-all">
                                {item.trackingNumber}
                              </code>
                            </div>
                            {item.trackingUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(item.trackingUrl!, '_blank')}
                                leftIcon={<ExternalLink className="w-4 h-4" />}
                                aria-label={`Rastrear envío ${item.trackingNumber} en nueva ventana`}
                              >
                                Rastrear
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Important dates */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {item.scheduledDate && (
                          <div>
                            <div className="text-gray-500 font-medium mb-1">Fecha programada</div>
                            <time className="text-gray-900 font-medium" dateTime={item.scheduledDate.toISOString()}>
                              {new Intl.DateTimeFormat('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              }).format(item.scheduledDate)}
                            </time>
                          </div>
                        )}
                        {item.deliveredAt && (
                          <div>
                            <div className="text-gray-500 font-medium mb-1">Fecha de entrega</div>
                            <time className="text-green-700 font-medium" dateTime={item.deliveredAt.toISOString()}>
                              {new Intl.DateTimeFormat('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              }).format(item.deliveredAt)}
                            </time>
                          </div>
                        )}
                      </div>

                      {/* Public notes */}
                      {item.publicNotes && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded" role="note">
                          <p className="text-sm text-blue-800">
                            <strong>Nota:</strong> {item.publicNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Support section */}
        <footer className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 text-primary-600 mr-2" aria-hidden="true" />
            ¿Necesitas ayuda?
          </h4>

          <p className="text-gray-600 mb-4">
            Estamos aquí para ayudarte con cualquier pregunta sobre tu entrega.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center p-3 bg-white rounded border border-gray-200">
              <Mail className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">Email</div>
                <a
                  href="mailto:support@wocarp.com"
                  className="text-sm text-primary-600 hover:text-primary-700 truncate block"
                >
                  support@wocarp.com
                </a>
              </div>
            </div>

            <div className="flex items-center p-3 bg-white rounded border border-gray-200">
              <Phone className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">WhatsApp</div>
                <a
                  href="https://chat.whatsapp.com/GIpv3nJzQLoBbwCbIBHdRy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  Únete al grupo
                </a>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Consejo:</strong> Incluye tu número ganador #{delivery.winningNumber.padStart(2, '0')} al contactarnos para un seguimiento más rápido.
            </p>
          </div>
        </footer>
      </div>
    </Modal>
  )
}

export const PrizeTrackingDashboard: React.FC<PrizeTrackingDashboardProps> = ({
  className = '',
  onClose
}) => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const {
    userDeliveries,
    isLoading,
    error,
    loadUserDeliveries,
    subscribeToUserDeliveries,
    clearError
  } = usePrizeDeliveryStore()

  const [selectedDelivery, setSelectedDelivery] = useState<UserPrizeTracking | null>(null)
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Initialize data loading and subscriptions
  useEffect(() => {
    if (!user?.id) return

    // Load initial data
    loadUserDeliveries(user.id)

    // Set up real-time subscription
    const unsubscribe = subscribeToUserDeliveries(user.id)

    return () => {
      unsubscribe()
    }
  }, [user?.id, loadUserDeliveries, subscribeToUserDeliveries])

  // Filter deliveries based on active filter and search query
  const filteredDeliveries = React.useMemo(() => {
    let filtered = userDeliveries

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(delivery => delivery.overallStatus === activeFilter)
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(delivery =>
        delivery.raffleTitle.toLowerCase().includes(query) ||
        delivery.winningNumber.includes(query) ||
        delivery.deliveries.some(d =>
          d.trackingNumber?.toLowerCase().includes(query) ||
          d.description.toLowerCase().includes(query)
        )
      )
    }

    return filtered
  }, [userDeliveries, activeFilter, searchQuery])

  // Get summary statistics
  const stats = React.useMemo(() => {
    const total = userDeliveries.length
    const pending = userDeliveries.filter(d => d.overallStatus === 'pending').length
    const inProgress = userDeliveries.filter(d => d.overallStatus === 'in-progress').length
    const completed = userDeliveries.filter(d => d.overallStatus === 'completed').length
    const hasOverdue = userDeliveries.some(d =>
      d.deliveries.some(item =>
        item.scheduledDate &&
        item.scheduledDate < new Date() &&
        !['delivered', 'cancelled'].includes(item.status)
      )
    )

    return { total, pending, inProgress, completed, hasOverdue }
  }, [userDeliveries])

  const handleDeliveryClick = (delivery: UserPrizeTracking) => {
    setSelectedDelivery(delivery)
  }

  const handleCloseDetail = () => {
    setSelectedDelivery(null)
  }

  // Clear error on mount
  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  if (!user) {
    return (
      <div className={`max-w-2xl mx-auto px-4 sm:px-6 ${className}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-6 p-4 bg-gray-100 rounded-full">
            <Package className="w-8 h-8 text-gray-400" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Acceso requerido
          </h2>
          <p className="text-gray-600 mb-6">
            Debes iniciar sesión para ver tus entregas de premios
          </p>
          <Button
            variant="primary"
            onClick={() => {
              // Trigger auth modal or redirect to login
              onClose?.()
            }}
          >
            Iniciar sesión
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={`max-w-6xl mx-auto px-4 sm:px-6 ${className}`}>
      {/* Clean Header */}
      <header className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-2"
                aria-label="Volver al inicio"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-primary-50 rounded-lg">
                <Package className="w-6 h-6 text-primary-600" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Mis entregas
                </h1>
                <p className="text-gray-600">
                  Seguimiento de tus premios de carpfishing
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" role="region" aria-label="Resumen de entregas">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-100 rounded">
                <Trophy className="w-5 h-5 text-gray-600" aria-hidden="true" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded">
                <Truck className="w-5 h-5 text-orange-600" aria-hidden="true" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-700">{stats.inProgress}</div>
                <div className="text-sm text-orange-600">En camino</div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded">
                <CheckCircle className="w-5 h-5 text-green-600" aria-hidden="true" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
                <div className="text-sm text-green-600">Recibidos</div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded">
                <Package className="w-5 h-5 text-blue-600" aria-hidden="true" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">{stats.pending}</div>
                <div className="text-sm text-blue-600">Preparando</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Clean Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtrar por estado</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" role="tablist" aria-label="Filtros de entregas de premios">
              {filterOptions.map((option) => {
                const Icon = option.icon
                const isActive = activeFilter === option.value
                return (
                  <button
                    key={option.value}
                    onClick={() => setActiveFilter(option.value)}
                    className={`
                      flex items-center space-x-3 p-3 rounded-lg border transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                      ${isActive ? option.activeClassName : option.className}
                    `}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="deliveries-list"
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="font-medium text-sm">{option.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Search */}
          <div className="lg:w-80">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Buscar</h3>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Search className="w-5 h-5 text-gray-400" aria-hidden="true" />
              </div>
              <Input
                type="text"
                placeholder="Sorteo, número ganador, seguimiento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                aria-label="Buscar en tus entregas de premios"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" role="alert">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-red-800 text-sm font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              aria-label="Cerrar mensaje de error"
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-6" aria-label="Cargando entregas">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="w-16 h-8 bg-gray-200 rounded"></div>
              </div>
              <div className="h-2 bg-gray-100 rounded mb-4"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="h-3 bg-gray-100 rounded"></div>
                <div className="h-3 bg-gray-100 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content Grid */}
      {!isLoading && (
        <main id="deliveries-list">
          {filteredDeliveries.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredDeliveries.map((delivery) => (
                  <motion.div
                    key={delivery.prizeDeliveryId}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <DeliveryCard
                      delivery={delivery}
                      onClick={() => handleDeliveryClick(delivery)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-16 px-4">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-6 p-4 bg-gray-100 rounded-full">
                  <Package className="w-8 h-8 text-gray-400" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {activeFilter === 'all' ? 'Sin entregas aún' : 'Sin resultados'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {activeFilter === 'all'
                    ? 'No tienes entregas de premios en este momento. ¡Participa en sorteos para ganar equipos de carpfishing!'
                    : `No hay entregas con el estado "${filterOptions.find(f => f.value === activeFilter)?.label.toLowerCase()}"`
                  }
                </p>
                {activeFilter === 'all' && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onClose?.()
                      navigate('/')
                    }}
                    leftIcon={<Trophy className="w-5 h-5" />}
                  >
                    Ver sorteos activos
                  </Button>
                )}
              </div>
            </div>
          )}
        </main>
      )}

      {/* Enhanced Delivery Detail Modal */}
      <AnimatePresence>
        {selectedDelivery && (
          <DeliveryDetailModal
            delivery={selectedDelivery}
            isOpen={true}
            onClose={handleCloseDetail}
          />
        )}
      </AnimatePresence>

    </div>
  )
}

export default PrizeTrackingDashboard